import type { PreloadedQuery } from 'react-relay';
import type { RouteResource } from '@atlassian/react-resource-router';
import { getTopNavRelayVariables } from '@jira/navigation-apps__resources/src/services/top-nav/topNav4UIStateResource.tsx';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import queryNode, {
	type useJiraAppChromeQuery as useJiraAppChromeQueryType,
} from './__generated__/useJiraAppChromeQuery.graphql.ts';
import { getSideNavRelayVariables } from './getSideNavRelayVariables.tsx';

export const appChromeResource: RouteResource<
	PreloadedQuery<useJiraAppChromeQueryType, Record<string, unknown>>
> = createRelayResource<useJiraAppChromeQueryType>({
	type: 'RESOURCE_TYPE_APP_CHROME',
	// Anonymous users and chromeless routes must not trigger this GraphQL query.
	// Use the resource-level `skip` predicate so `loadQuery` is never invoked for
	// them — `getQuery` will not run when `skip` returns `true`.
	skip: (routerContext, { tenantContext: { isAnonymous } }) => {
		return shouldSkipAppChromeQuery({
			isAnonymous,
			// Chromeless routes render no top nav and no side nav, so this
			// `isCritical` query would be SSR-blocking work whose data is never
			// read. `route.layout` is the same plain-object layout config that
			// drives the renderer's `useIsChromeless()`, so the two cannot disagree.
			isChromeless: routerContext.route.layout?.isChromeless === true,
		});
	},
	getQuery: (routerContext, { tenantContext }) => {
		const { isAnonymous } = tenantContext;
		const sideNavRelayVariables = getSideNavRelayVariables(routerContext, tenantContext);
		return {
			parameters: queryNode,
			variables: {
				// NAVX-5414: these are the same variables the standalone side-nav resource
				// uses. Keep the helper output authoritative so the two operations cannot
				// silently fetch different data as side-nav requirements evolve.
				...sideNavRelayVariables,
				// The old record is per-user, so there is nothing to migrate without an owner.
				// Its entity ARI is supplied by the shared side-nav variable helper above.
				fetchOldEntityRecord: Boolean(sideNavRelayVariables.ownerAri),
				skipNavPlatSidebarData: isAnonymous,
				// The GlobalRoot query also spreads `...TopNavigation_slotsQuery` and
				// (via ...AppChrome) GTN's Notifications fragment. Those children read
				// these as root operation variables — including collabContextRoutingAri
				// for the unread-notification count. This resource is registered only
				// behind `fg('navx-global-top-nav-jira')` in `getNavigationResources.tsx`,
				// so fetch the unit profile for GTN's platform-owned AppSwitcher without
				// making it depend on NAVX-5411's legacy measurement experiment. All
				// other callers retain that experiment-driven behavior.
				...getTopNavRelayVariables(routerContext, tenantContext, {
					forceAppSwitcherUnitProfile: true,
				}),
			},
			options: { fetchPolicy: 'store-or-network' },
		};
	},
	isCritical: true,
});

/**
 * Whether the app-chrome query should be skipped for this render.
 */
function shouldSkipAppChromeQuery({
	isAnonymous,
	isChromeless,
}: {
	isAnonymous: boolean;
	isChromeless: boolean;
}): boolean {
	return isAnonymous || isChromeless;
}
